/**
 * Progressive toolbar overflow — pure measurement helpers (testable without DOM).
 */

/** Lower number = keep visible longer; higher = overflow first. */
export const TOOLBAR_OVERFLOW_PRIORITY = {
  'reply-all': 10,
  'internal-note': 20,
  'mark-unread': 30,
  star: 40,
  'move-folder': 50,
  'tag-picker': 60,
  'task-new': 70,
  'task-link': 80,
  status: 90,
  assign: 100,
}

export const TOOLBAR_DIVIDER_AFTER = {
  'divider-state': 'internal-note',
  'divider-move': 'move-folder',
  'divider-tags': 'tag-picker',
  'divider-tasks': 'task-link',
}

/**
 * Pick item ids that should move into the overflow menu.
 *
 * @param {Array<{ id: string, width: number, overflowable?: boolean }>} items
 * @param {number} availableWidth — space for visible toolbar items (overflow btn excluded)
 * @returns {Set<string>}
 */
export function computeOverflowIds(items, availableWidth) {
  const hidden = new Set()
  if (availableWidth <= 0) {
    items.filter(i => i.overflowable !== false).forEach(i => hidden.add(i.id))
    return expandDividerHidden(hidden)
  }

  const overflowable = items
    .filter(i => i.overflowable !== false)
    .sort((a, b) => {
      const pa = TOOLBAR_OVERFLOW_PRIORITY[a.id] ?? 0
      const pb = TOOLBAR_OVERFLOW_PRIORITY[b.id] ?? 0
      return pb - pa
    })

  function visibleWidth() {
    return items
      .filter(i => !hidden.has(i.id))
      .reduce((sum, i) => sum + (i.width || 0), 0)
  }

  for (const item of overflowable) {
    if (visibleWidth() <= availableWidth) break
    hidden.add(item.id)
  }

  return expandDividerHidden(hidden)
}

function expandDividerHidden(hidden) {
  const next = new Set(hidden)
  Object.entries(TOOLBAR_DIVIDER_AFTER).forEach(([dividerId, afterItemId]) => {
    if (hidden.has(afterItemId)) next.add(dividerId)
  })
  return next
}

export const OVERFLOW_BUTTON_WIDTH = 36

/** Status + assign — only overflow when the end group genuinely does not fit. */
export const TOOLBAR_END_GROUP_IDS = ['status', 'assign']

/**
 * @deprecated Prefer coalesceEndGroupOverflowIfNeeded — only use when end group must move into ⋮.
 * @param {Set<string>} overflowIds
 * @returns {Set<string>}
 */
export function coalesceEndGroupOverflow(overflowIds) {
  if (overflowIds.size === 0) return overflowIds
  const next = new Set(overflowIds)
  TOOLBAR_END_GROUP_IDS.forEach(id => next.add(id))
  return next
}

/**
 * Move status + assign into the overflow menu only when their visible width exceeds the budget.
 *
 * @param {Set<string>} overflowIds — track items already overflowing
 * @param {number} endGroupWidth — measured width of status + assign (+ ⋮ when shown)
 * @param {number} endBudget — horizontal space available for the end group (toolbar − track)
 * @returns {Set<string>}
 */
export function coalesceEndGroupOverflowIfNeeded(overflowIds, endGroupWidth, endBudget) {
  if (overflowIds.size === 0) return overflowIds
  if (endGroupWidth <= endBudget) return overflowIds
  return coalesceEndGroupOverflow(overflowIds)
}